import React, { useState, useCallback, useEffect } from 'react';
import defaultTo from 'lodash/defaultTo';
import uniq from 'lodash/uniq';
import compact from 'lodash/compact';
import { token } from '@atlaskit/tokens';
import { Flex, Box, xcss, Text } from '@atlaskit/primitives';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import { createInvoiceGroup } from '@atlassian/cssxp-proxies';
import { ProxyGraphClients, abgGraphqlClient } from '@atlassian/temp-client-utils';
import { createContactRelationship } from '@atlassian/temp-proxies';
import { CreateInvoiceGroupRequest } from '@atlassian/cssxp-types-billing';
import Button, { LoadingButton } from '@atlaskit/button';
import { ProgressIndicator } from '@atlaskit/progress-indicator';
import Form, { FormHeader, FormSection, RequiredAsterisk } from '@atlaskit/form';
import { useApplicationContext } from '@css-xp/base/common-context';
import { CreateContactRelationshipsRequest } from '@css-xp/contact-resources/src/constant';
import {
  useFormSubmit,
  useNotification,
  ErrorComponents,
  InfoPanel,
  successNotificationBuilder,
  NotificationTypes,
} from '@css-xp/base';
import { CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';
import { withApollo } from '@css-xp/base/graphql-provider';
import { HelpContent, getCurrencyByCountry } from '@css-xp/ppc-common';
import {
  BillingAdmin,
  FetchBillingAdminsForTransactionAccountPpcQuery,
  FetchBillingAdminsForTransactionAccountPpcQueryVariables,
} from '@css-xp/ppc-graphtypes';
import { FormData } from '../components/billing-profile-components';
import { fetchBillingAdminsForTransactionAccountPPC } from '../../../../queries';
import { CustomerDetailsStep } from './components/customer-details-step';
import { getError, isValidAddress } from '../sidebar/create-billing-profile';
import { PartnerDetailsStep } from './components/partner-details-step';
import { FinalizeStep } from './components/final-step';
import { useContactServicePayload } from '@css-xp/contact-resources';

enum FormStages {
  CUSTOMER_DETAILS_STEP = 'Customer details',
  PARTNER_DETAILS_STEP = 'Partner details',
  FINAL_STEP = 'Final step',
}

export enum FormTrackerSteps {
  CUSTOMER_DETAILS_STEP = 'CUSTOMER_DETAILS_STEP',
  PARTNER_DETAILS_STEP = 'PARTNER_DETAILS_STEP',
  FINAL_STEP = 'FINAL_STEP',
}

const formFooterStyles = xcss({
  marginTop: 'space.200',
});

let stages = Object.values(FormStages);

// transforming enum to array for adding and removing progress step counts runtime based on FF state
let defaultFormTrackerStep = Object.values(FormTrackerSteps);

enum ContentEnum {
  SOLD_TO_ADDRESS_LABEL = 'SOLD_TO_ADDRESS_LABEL',
}

type ContentString = {
  [key in ContentEnum]?: string;
};

interface ContactDetails {
  primaryCustomerContactAccountIds: string[];
  secondaryCustomerContactAccountIds: string[];
  primaryCustomerContactAccountEmails: string[];
  secondaryCustomerContactAccountEmails: string[];
}

export const CreateBillingProfilePaneV2 = withApollo(
  /// new bp creation flow
  ({
    transactionAccountId,
    transactionAccountDetails,
    refetch,
    soldToAddresses,
    defaultSoldToAddress,
    onSubmit,
    isSubmitLoading,
    closeModal,
    existingBillingProfileDetails,
    formStages,
    initialCurrentProgressIndex,
    onBackCustomerClick,
    showMonthlyDefaultSelected,
    shouldHideFormHeader = false,
    onNext,
    onBack,
    showDefaultProgressIndicator,
    contentString,
    setIsNewBillingProfileCreated,
    handleAddNewAddressClick,
    showAddNewAddressOption,
    billingProfileFormData,
    contactDetails,
    shouldRenderForInternalUse,
    shouldShowACHPaymentMethod,
    validDomains,
    isCustomerContactsEditable,
  }: {
    transactionAccountId: string;
    transactionAccountDetails?: any;
    refetch?: () => Promise<ApolloQueryResult<unknown>>;
    soldToAddresses?: CommerceAddress[];
    defaultSoldToAddress?: CommerceAddress;
    validDomains?: string[] | null | undefined;
    onSubmit?: (_requestForm: CreateInvoiceGroupRequest, _contactServicePayload: any) => void;
    isSubmitLoading?: boolean;
    isTermPaymentSelectionDisabled?: boolean;
    onBackCustomerClick?: (billingProfileData: FormData, contactDetails: ContactDetails) => void;
    closeModal: () => void;
    existingBillingProfileDetails?: any;
    formStages?: string[];
    initialCurrentProgressIndex?: number;
    showMonthlyDefaultSelected?: boolean;
    shouldHideFormHeader?: boolean;
    onBack?: () => void;
    onNext?: () => void;
    showDefaultProgressIndicator?: boolean;
    contentString?: ContentString;
    setIsNewBillingProfileCreated?: (_isNewBillingProfileCreated: boolean) => void;
    handleAddNewAddressClick?: () => void;
    showAddNewAddressOption?: boolean;
    billingProfileFormData?: FormData;
    contactDetails?: ContactDetails;
    shouldRenderForInternalUse?: boolean;
    shouldShowACHPaymentMethod?: boolean;
    isCustomerContactsEditable?: boolean;
  }) => {
    const partnerData = useApplicationContext();
    const { getCreateContactRelationshipsPayload } = useContactServicePayload();
    const selectedPartnerAccountData = partnerData?.partnerAccounts.find(
      ({ transactionAccountId: txaId }: { transactionAccountId: string }) => txaId === transactionAccountId
    );

    if (initialCurrentProgressIndex) {
      defaultFormTrackerStep = formStages as FormTrackerSteps[];
      stages = formStages as FormStages[];
    } else {
      defaultFormTrackerStep = Object.values(FormTrackerSteps);
      stages = Object.values(FormStages);
    }

    const [shipToAddress, setShipToAddress] = useState<CommerceAddress | undefined | null>(defaultSoldToAddress);
    const [currentProgressIndex, setCurrentProgressIndex] = useState<number>(initialCurrentProgressIndex || 0);
    const [billingProfileData, setBillingProfileData] = useState<FormData>(
      billingProfileFormData ? billingProfileFormData : {}
    );
    const [isNotValidAddress, setIsNotValidAddress] = useState<boolean>(false);

    const [primaryCustomerContactAccountIds, setPrimaryCustomerContactAccountIds] = useState<Array<string>>(
      contactDetails ? contactDetails.primaryCustomerContactAccountIds : []
    );
    const [primaryCustomerContactAccountEmails, setPrimaryCustomerContactAccountEmails] = useState<Array<string>>(
      contactDetails ? contactDetails.primaryCustomerContactAccountEmails : ['']
    );
    const [secondaryCustomerContactAccountIds, setSecondaryCustomerContactAccountIds] = useState<Array<string>>(
      contactDetails ? contactDetails.secondaryCustomerContactAccountIds : []
    );
    const [secondaryCustomerContactAccountEmails, setSecondaryCustomerContactAccountEmails] = useState<Array<string>>(
      contactDetails ? contactDetails.secondaryCustomerContactAccountEmails : ['']
    );
    const [billingAdmins, setBillingAdmins] = useState<BillingAdmin[]>([]);
    const [otherContacts, setOtherContacts] = useState<BillingAdmin[]>([]);
    const [keyContact, setKeyContact] = useState([]);

    const ppcBffGraphqlClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.PPC_BFF), []);
    const availablePaymentMethods = transactionAccountDetails?.transactionAccount?.availablePaymentMethods?.nodes;
    const termPaymentStatus = transactionAccountDetails?.transactionAccount?.billingAccountsList?.status;

    const { data: billingAdminsData, loading: billingAdminsLoading } = useQuery<
      FetchBillingAdminsForTransactionAccountPpcQuery,
      FetchBillingAdminsForTransactionAccountPpcQueryVariables
    >(fetchBillingAdminsForTransactionAccountPPC, {
      variables: {
        id: transactionAccountId,
        billingAdminOptions: {
          partnerAccountId: defaultTo(selectedPartnerAccountData?.id, ''),
          billingAdminIds: [],
        },
      },
      client: ppcBffGraphqlClient,
      displayName: `fetchBillingAdmins`,
      notifyOnNetworkStatusChange: true,
      nextFetchPolicy: 'cache-first',
      skip: !selectedPartnerAccountData?.id,
    });

    const billingAdminConnection = billingAdminsData?.transactionAccount?.billingAdmins;
    const txaBillingAdmins = billingAdminConnection?.edges || [];

    const FormTrackerStep = defaultFormTrackerStep.reduce((acc, step, index) => {
      acc[step] = index;
      return acc;
    }, {} as Record<string, number>);

    const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

    const { error, isProcessing, submitForm, complete, data } = useFormSubmit<CreateInvoiceGroupRequest, any>({
      submitFormAction: createInvoiceGroup,
    });

    useEffect(() => {
      if (data) {
        let payloadVariables = {
          createContactIds: uniq(compact([...primaryCustomerContactAccountIds, ...secondaryCustomerContactAccountIds])),
          primaryContactId: primaryCustomerContactAccountIds[0],
          createBillingContactIds: billingAdmins.map((contact) => contact.atlassianAccountId) as string[],
        };

        if (shouldRenderForInternalUse) {
          const keyContactId = (keyContact as any)?.atlassianAccountId;
          const otherContactIds = otherContacts.map((contact) => contact.atlassianAccountId);
          const allIds = uniq(compact([keyContactId, ...otherContactIds]));
          payloadVariables = {
            createContactIds: allIds,
            primaryContactId: keyContactId as string,
            createBillingContactIds: allIds,
          };
        }

        const createPayload = getCreateContactRelationshipsPayload({
          invoiceGroupId: data?.id,
          transactionAccountId: transactionAccountId,
          ...payloadVariables,
        });
        contactServiceHandler(createPayload as CreateContactRelationshipsRequest);
      }
    }, [data]);

    const contactServiceHandler = async (contactPayLoad: CreateContactRelationshipsRequest) => {
      await createContactRelationship(contactPayLoad);
    };
    const currencyValue = getCurrencyByCountry(
      shouldRenderForInternalUse ? selectedPartnerAccountData?.billTo?.country : shipToAddress?.country
    );

    const submitCreateInvoiceGroupForm = useCallback(
      (formData: FormData) => {
        setIsNotValidAddress(false);
        //  check if form data has any value other than defaultPaymentMethod
        //  if yes then merge it with billingProfileData
        const isFormDataExist = Object.keys(formData).some(
          (key) => key !== 'defaultPaymentMethod' && (formData as any)[key] !== undefined
        );
        const finalFormData = { ...billingProfileData, ...(isFormDataExist ? formData : {}) };
        setBillingProfileData(finalFormData);

        if (
          currentProgressIndex === FormTrackerStep.CUSTOMER_DETAILS_STEP &&
          !isValidAddress(finalFormData?.soldToAddress)
        ) {
          setIsNotValidAddress(true);
          return;
        }

        if (currentProgressIndex < stages.length - 1) {
          setCurrentProgressIndex(currentProgressIndex + 1);
          return;
        }

        const requestForm: CreateInvoiceGroupRequest = {
          transactionAccountId: transactionAccountId,
          data: {
            name: defaultTo(finalFormData.invoiceGroupName, ''),
            currency: currencyValue,
          },
        };

        if (finalFormData.defaultPaymentMethod) {
          requestForm.data = {
            ...requestForm.data,
            defaultPaymentMethod: finalFormData.defaultPaymentMethod,
          };
        }

        // check if we selected an existing shipTo and not made any updates to it
        if (shipToAddress && shipToAddress.id) {
          // send existing shipToPartyId to update it
          requestForm.data.shipToPartyId = shipToAddress?.id;
        }
        if (finalFormData.soldToAddress === null) {
          requestForm.data.shipToPartyId = undefined;
        }

        if (shouldRenderForInternalUse) {
          requestForm.data.shipToParty = null;
        }

        if (onSubmit) {
          const contactServicePayload = {
            transactionAccountId: transactionAccountId,
            createContactIds: uniq(
              compact([...primaryCustomerContactAccountIds, ...secondaryCustomerContactAccountIds])
            ),
            primaryContactId: primaryCustomerContactAccountIds[0],
            createBillingContactIds: billingAdmins.map((contact) => contact.atlassianAccountId) as string[],
          };
          onSubmit(requestForm, contactServicePayload);
        } else {
          submitForm(requestForm);
        }
      },
      [billingProfileData, currentProgressIndex, shipToAddress, submitForm, transactionAccountId, onSubmit]
    );

    if (complete && !notificationSent) {
      const successMessage = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('create-invoice-group-modal-success')
        .withHeader('Billing profile created')
        .withMessage('Refresh the page or check back in some time.')
        .withDismissAfter(5000)
        .build();
      sendSuccessNotification(successMessage);
      setNotificationSent(true);
      closeModal();
      setIsNewBillingProfileCreated?.(true);
      if (refetch) {
        refetch();
      }
    }

    const getFormStep = useCallback(
      (formProps: {
        disabled: boolean;
        getValues: () => FormData;
        setFieldValue: (_name: string, _value: any) => void;
      }) => {
        switch (currentProgressIndex) {
          case FormTrackerStep.CUSTOMER_DETAILS_STEP: {
            return (
              <CustomerDetailsStep
                onFieldValueChange={formProps.setFieldValue}
                onShipToAddressUpdate={setShipToAddress}
                billingProfileData={billingProfileData}
                customerAddresses={soldToAddresses}
                defaultSelectedAddress={shipToAddress}
                isMultiCurrencyChangesEnabled={true}
                primaryCustomerContactAccountIds={primaryCustomerContactAccountIds}
                setPrimaryCustomerContactAccountIds={setPrimaryCustomerContactAccountIds}
                primaryCustomerContactAccountEmails={primaryCustomerContactAccountEmails}
                setPrimaryCustomerContactAccountEmails={setPrimaryCustomerContactAccountEmails}
                secondaryCustomerContactAccountIds={secondaryCustomerContactAccountIds}
                setSecondaryCustomerContactAccountIds={setSecondaryCustomerContactAccountIds}
                secondaryCustomerContactAccountEmails={secondaryCustomerContactAccountEmails}
                setSecondaryCustomerContactAccountEmails={setSecondaryCustomerContactAccountEmails}
                shouldHideFormHeader={shouldHideFormHeader}
                soldToAddressFieldLabel={contentString?.SOLD_TO_ADDRESS_LABEL}
                isSoldToAddressAdded={!!defaultSoldToAddress}
                handleAddNewAddressClick={handleAddNewAddressClick}
                showAddNewAddressOption={showAddNewAddressOption}
                shouldRenderForInternalUse={shouldRenderForInternalUse}
                billingAdmins={billingAdmins}
                setBillingAdmins={shouldRenderForInternalUse ? (setKeyContact as any) : setBillingAdmins}
                txaBillingAdmins={txaBillingAdmins}
                billingAdminsLoading={billingAdminsLoading}
                setOtherContacts={setOtherContacts}
                validDomains={validDomains}
                isCustomerContactsEditable={isCustomerContactsEditable}
              />
            );
          }
          case FormTrackerStep.PARTNER_DETAILS_STEP: {
            return (
              <PartnerDetailsStep
                availablePaymentMethods={availablePaymentMethods}
                onFieldValueChange={formProps.setFieldValue}
                selectedPaymentId={billingProfileData?.defaultPaymentMethod}
                termPaymentStatus={termPaymentStatus}
                isMultiCurrencyChangesEnabled={true}
                billingAdmins={billingAdmins}
                setBillingAdmins={setBillingAdmins}
                txaBillingAdmins={txaBillingAdmins}
                billingAdminsLoading={billingAdminsLoading}
                existingBillingProfileDetails={existingBillingProfileDetails}
                billingProfileData={billingProfileData}
                transactionAccountId={transactionAccountId}
                showMonthlyDefaultSelected={showMonthlyDefaultSelected}
                shouldHideFormHeader={shouldHideFormHeader}
                shouldRenderForInternalUse={shouldRenderForInternalUse}
                shouldShowACHPaymentMethod={shouldShowACHPaymentMethod}
              />
            );
          }
          case FormTrackerStep.FINAL_STEP: {
            return (
              <FinalizeStep
                billingProfileData={billingProfileData}
                availablePaymentMethods={availablePaymentMethods}
                primaryCustomerContactAccountEmails={primaryCustomerContactAccountEmails}
                secondaryCustomerContactAccountEmails={secondaryCustomerContactAccountEmails}
                billingAdmins={billingAdmins}
                existingBPNameList={existingBillingProfileDetails?.map((billingProfile: any) => billingProfile.name)}
                shouldHideFormHeader={shouldHideFormHeader}
                shouldRenderForInternalUse={shouldRenderForInternalUse}
                currency={currencyValue}
                keyContact={keyContact as BillingAdmin}
                otherContacts={otherContacts}
              />
            );
          }
        }
      },
      [
        currentProgressIndex,
        transactionAccountId,
        shipToAddress,
        billingProfileData,
        availablePaymentMethods,
        termPaymentStatus,
        primaryCustomerContactAccountIds,
        secondaryCustomerContactAccountIds,
        billingAdmins,
        txaBillingAdmins, // Added txaBillingAdmins so that Key contact list gets populated due to re-render
      ]
    );

    const hasShipToAddressBeenSelected = shouldRenderForInternalUse ? true : Boolean(shipToAddress);
    const shouldDisableNextInCustomerContactStep =
      currentProgressIndex === FormTrackerStep.CUSTOMER_DETAILS_STEP &&
      (shouldRenderForInternalUse
        ? keyContact?.length === 0
        : primaryCustomerContactAccountIds.length === 0 ||
          primaryCustomerContactAccountIds.findIndex((id) => !id) >= 0);

    return (
      <Form onSubmit={submitCreateInvoiceGroupForm}>
        {({ formProps, submitting, dirty, ...props }) => (
          <form {...formProps} className="create-billing-profile-form" data-testid="create-billing-profile-form">
            {!shouldHideFormHeader && (
              <Flex justifyContent="space-between" xcss={xcss({ padding: 'space.300', paddingBottom: 'space.0' })}>
                <FormHeader
                  title={
                    shouldRenderForInternalUse ? 'Create billing profile for internal usage' : 'Add billing profile'
                  }
                >
                  {shouldRenderForInternalUse && (
                    <Flex xcss={xcss({ marginTop: 'space.negative.100' })}>
                      {/* Negative margin is provided to match the design as we have less height. Note: it is different to Atlaskit value. */}
                      <Text color="color.text.subtle">
                        Required fields are marked with an asterisk <RequiredAsterisk />
                      </Text>
                    </Flex>
                  )}
                </FormHeader>
                <Button appearance="subtle" onClick={closeModal} testId="add-customer-modal-close-button-with-icon">
                  <CrossIcon size="small" label="Close Modal" primaryColor={token('color.text.subtle')} />
                </Button>
              </Flex>
            )}
            {(error || isNotValidAddress) && (
              <FormSection>
                {error && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to create a new invoice group for the following reason"
                        message={error}
                        helpContent={<HelpContent />}
                      />
                    }
                  />
                )}
                {isNotValidAddress && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to create a new invoice group for the following reason"
                        message={getError(billingProfileData)}
                        helpContent={<HelpContent />}
                      />
                    }
                  />
                )}
              </FormSection>
            )}
            <Flex
              direction="column"
              justifyContent="space-between"
              xcss={xcss({
                height: shouldRenderForInternalUse ? '88%' : '90%',
                padding: 'space.300',
                paddingTop: 'space.0',
              })}
            >
              {getFormStep(props)}
              <Box id="footer" xcss={formFooterStyles}>
                <Flex justifyContent="space-between" alignItems="center">
                  <Button
                    isDisabled={currentProgressIndex === 0}
                    onClick={() => {
                      if (onBackCustomerClick && currentProgressIndex === FormTrackerStep.CUSTOMER_DETAILS_STEP) {
                        onBackCustomerClick(billingProfileData, {
                          primaryCustomerContactAccountIds,
                          secondaryCustomerContactAccountIds,
                          primaryCustomerContactAccountEmails,
                          secondaryCustomerContactAccountEmails,
                        });
                      }
                      if (onBack) {
                        onBack();
                      }
                      setCurrentProgressIndex((prevState) => prevState - 1);
                    }}
                  >
                    Back
                  </Button>
                  <ProgressIndicator
                    selectedIndex={currentProgressIndex}
                    values={formStages ? formStages : defaultFormTrackerStep}
                    appearance={showDefaultProgressIndicator ? 'default' : 'primary'}
                    size={showDefaultProgressIndicator ? 'default' : 'large'}
                  />
                  <LoadingButton
                    appearance={
                      currentProgressIndex === stages.length - 1 || currentProgressIndex === stages.length
                        ? 'primary'
                        : 'default'
                    }
                    type="submit"
                    isDisabled={!hasShipToAddressBeenSelected || shouldDisableNextInCustomerContactStep}
                    isLoading={submitting || isProcessing || isSubmitLoading}
                    testId={submitting ? 'loading-button' : 'create-button'}
                    onClick={() => {
                      if (
                        onNext &&
                        (currentProgressIndex !== stages.length - 1 || currentProgressIndex !== stages.length)
                      ) {
                        onNext();
                      }
                    }}
                  >
                    {currentProgressIndex === stages.length - 1 || currentProgressIndex === stages.length
                      ? 'Create'
                      : 'Next'}
                  </LoadingButton>
                </Flex>
              </Box>
            </Flex>
          </form>
        )}
      </Form>
    );
  }
);
